import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAnalytics } from '@center-technologies/analytics';
import {
  deleteSphereAvatar,
  globalSphereCache,
  rereadSphereAvatar,
  resetSphereGlyph,
  setSphereGlyph,
  uploadSphereAvatar,
} from '@center-platform/spheres/cache';
import { readOwnSphereGlyph, resolveSphereGlyph } from '@center-platform/spheres/glyph';
import type { ResolvedSphereGlyph } from '@center-platform/spheres/glyph';
import type { GlyphColor, SphereOwnGlyph } from 'types/glyph';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { getSphereSchemaConfig } from '../api/schemaApi';

export type SphereVisualChange =
  | 'glyph'
  | 'colour'
  | 'glyph_and_colour'
  | 'reset'
  | 'picture_added'
  | 'picture_replaced'
  | 'picture_removed'
  | 'undo';

/**
 * What a failed write means to the person, not its HTTP shape:
 * - `readOnly`: the server says they cannot edit this sphere, so the controls go static;
 * - `unavailable`: permissions could not be checked (503 `permission_service_unreachable`, or a 503 with
 *   no code); the action can be retried;
 * - `serviceUnavailable`: a service the write needs is down (any other 503, such as `attachments_unavailable`);
 *   the action can be retried;
 * - `slotUnavailable`: the type no longer declares an avatar slot (400 SLOT_NOT_AVAILABLE);
 * - `refused`: any other 403, such as a ledger write-tier gate with its `reason`; a retry gets the same answer;
 * - `invalid`: the server rejected what was sent (400, 413, 415); a retry gets the same answer;
 * - `rateLimited`: too many picture writes in a short time (429 `RATE_LIMITED`, the upload and removal
 *   limiter); a retry before `retryAt` gets the same answer;
 * - `throttled`: any other 429, such as the service-wide request limit, which any write can meet; a retry
 *   right away gets the same answer;
 * - `notFound`: the sphere no longer exists (404); nothing can be saved to it;
 * - `save`: the write did not reach the server or failed there (network, 5xx); it can be retried.
 */
export type SphereVisualErrorKind =
  | 'readOnly'
  | 'unavailable'
  | 'serviceUnavailable'
  | 'slotUnavailable'
  | 'refused'
  | 'invalid'
  | 'rateLimited'
  | 'throttled'
  | 'notFound'
  | 'save';

/** Whether re-running the same write could succeed. */
export function isRetryableSphereVisualError(kind: SphereVisualErrorKind): boolean {
  return kind === 'save' || kind === 'unavailable' || kind === 'serviceUnavailable';
}

/** The kind of write: the glyph and colour, a picture upload, or a picture removal. */
export type SphereVisualOperation = 'glyph' | 'upload' | 'remove';

export interface SphereVisualError {
  kind: SphereVisualErrorKind;
  /** The write that failed, so the message names what the person tried to do. */
  operation: SphereVisualOperation;
  /** The server's stable denial code, when it sent one (e.g. `aspect_read_only`). */
  reason?: string;
  /** For `rateLimited`: when the limit resets (ISO 8601), when the server said. */
  retryAt?: string;
}

export type SphereVisualStatus = 'idle' | 'saving' | 'error' | 'readOnly';

/** What a completed write saved. */
export interface SphereVisualSaved {
  /** Identifies this write among the hook's completed writes; see `UseSphereVisualResult.lastSaveId`. */
  id: number;
  change: SphereVisualChange;
  /** Whether `undo()` reverts this write. Replacing a picture deletes the old one, so it cannot be undone. */
  undoable: boolean;
}

/** The slice of a schema type the visual control reads. */
export interface SphereVisualSchemaType {
  system_name: string;
  glyph?: string | null;
  glyph_color?: GlyphColor | null;
}

export interface SphereVisualSphere {
  id: string;
  properties?: Record<string, unknown> | null;
}

/** The sphere's picture as the sphere cache holds it. */
export interface SphereVisualPicture {
  url?: string;
  /** True while a picture write is in flight. */
  pending: boolean;
  /** True until the sphere cache has answered for this sphere, so the mark is not yet known. */
  resolving: boolean;
  /**
   * True from a successful upload until a read returns the picture: an uploaded picture is
   * scanned before it is served, so until then the sphere has none to show.
   */
  checking: boolean;
  /** True once `checking` has stopped re-reading; the picture shows the next time the sphere is opened. */
  checkStopped: boolean;
}

/** What the type's avatar slot accepts, as schema-service declares it; an absent field is not limited. */
export interface SphereVisualPictureLimits {
  mimeTypes?: string[];
  maxSize?: number;
}

export interface PickerValue {
  glyph?: string | null;
  glyphColor?: GlyphColor | null;
}

export interface UseSphereVisualOptions {
  /**
   * Called with the sphere's id once a write has changed what the server stores for it, and again
   * when a picture being checked first reads back. The views behind the control hold their own copy
   * of the sphere; this is how they learn to refresh it.
   */
  onSphereChanged?: (sphereId: string) => void;
}

/** Resolves to what was saved, or `null` when the write failed or there was nothing to write. */
export type SphereVisualWrite = Promise<SphereVisualSaved | null>;

export interface UseSphereVisualResult {
  resolved: ResolvedSphereGlyph;
  own: SphereOwnGlyph | null;
  picture: SphereVisualPicture;
  status: SphereVisualStatus;
  /** The write in flight, or `null` when none is. */
  operation: SphereVisualOperation | null;
  error: SphereVisualError | null;
  /** True once schema-service says the type declares an `avatar` slot; false while that is unknown. */
  canUploadPicture: boolean;
  /** The avatar slot's limits, once known; `null` while `canUploadPicture` is false. */
  pictureLimits: SphereVisualPictureLimits | null;
  canUndo: boolean;
  /**
   * The `id` of the latest completed write, or `null` before any. `undo()` reverts that write only,
   * so an Undo offered for an earlier one must be withdrawn once this moves past it.
   */
  lastSaveId: number | null;
  /** Writes only the fields of `next` that differ from the effective glyph and colour. */
  apply: (next: PickerValue) => SphereVisualWrite;
  setGlyph: (glyph: string) => SphereVisualWrite;
  setColour: (color: GlyphColor) => SphereVisualWrite;
  reset: () => SphereVisualWrite;
  uploadPicture: (file: File) => SphereVisualWrite;
  removePicture: () => SphereVisualWrite;
  undo: () => SphereVisualWrite;
  /** Re-runs the last failed write. */
  retry: () => SphereVisualWrite;
}

interface ErrorLike {
  status?: number;
  details?: unknown;
  reason?: unknown;
  /** `PatchSpherePropertiesError`'s server code: the body's `error`, never a transport message. */
  serverCode?: unknown;
  response?: { status?: number; data?: { error?: unknown; reason?: unknown; details?: { resetAt?: unknown } } };
}

/** Maps a failed visual write to what it means; see {@link SphereVisualErrorKind}. */
export function classifySphereVisualError(err: unknown): SphereVisualErrorKind {
  const e = (err ?? {}) as ErrorLike;
  const status = e.status ?? e.response?.status;
  const code = e.response?.data?.error ?? e.details;
  if (status === 503) {
    // A permission check that could not be answered sends its code; a 503 with no code is read the
    // same way. Any other code names another service the write needs.
    const serverCode = e.response?.data?.error ?? e.serverCode;
    return serverCode === undefined || serverCode === 'permission_service_unreachable'
      ? 'unavailable'
      : 'serviceUnavailable';
  }
  if (status === 403) return code === 'requires_edit' || code === 'forbidden' ? 'readOnly' : 'refused';
  if (status === 400 && code === 'SLOT_NOT_AVAILABLE') return 'slotUnavailable';
  if (status === 400 || status === 413 || status === 415) return 'invalid';
  if (status === 429) return code === 'RATE_LIMITED' ? 'rateLimited' : 'throttled';
  if (status === 404) return 'notFound';
  return 'save';
}

/** When a rate-limited write may be tried again, from the 429 body's `details.resetAt`, if it is a valid date. */
export function sphereVisualErrorRetryAt(err: unknown): string | undefined {
  const resetAt = ((err ?? {}) as ErrorLike).response?.data?.details?.resetAt;
  return typeof resetAt === 'string' && !Number.isNaN(Date.parse(resetAt)) ? resetAt : undefined;
}

/** The server's stable denial code on a failed write, when it sent one. */
export function sphereVisualErrorReason(err: unknown): string | undefined {
  const e = (err ?? {}) as ErrorLike;
  const reason = e.reason ?? e.response?.data?.reason;
  return typeof reason === 'string' ? reason : undefined;
}

/**
 * How long to wait before each re-read while an uploaded picture is being checked. Bounded: once
 * the last one finds no picture, the hook stops and says the picture shows on the next open.
 */
export const PICTURE_CHECK_BACKOFF_MS: readonly number[] = [2000, 5000, 10000, 20000];

type UndoEntry =
  | { kind: 'glyph'; restore: SphereOwnGlyph }
  | { kind: 'picture_added' };

function applyPatch(own: SphereOwnGlyph | null, patch: SphereOwnGlyph): SphereOwnGlyph | null {
  const next: SphereOwnGlyph = { ...own };
  if ('glyph' in patch) {
    if (patch.glyph) {
      next.glyph = patch.glyph;
      next.glyphKind = patch.glyph.includes(':') ? 'iconify' : 'letter';
    } else {
      delete next.glyph;
      delete next.glyphKind;
    }
  }
  if ('glyphColor' in patch) {
    if (patch.glyphColor) next.glyphColor = patch.glyphColor;
    else delete next.glyphColor;
  }
  return Object.keys(next).length > 0 ? next : null;
}

function sameColor(a: GlyphColor | null | undefined, b: GlyphColor | null | undefined): boolean {
  return !!a && !!b && a.kind === b.kind && a.value === b.value;
}

function glyphChangeOf(patch: SphereOwnGlyph): SphereVisualChange {
  const glyphChanged = 'glyph' in patch;
  const colourChanged = 'glyphColor' in patch;
  if (glyphChanged && colourChanged) return 'glyph_and_colour';
  return glyphChanged ? 'glyph' : 'colour';
}

/**
 * The sphere's picture and glyph state, and the writes that change it. Mount it ONCE per sphere and
 * hand the result to every control that shows the mark, so they all show the same state.
 *
 * Writes run one at a time in the order they were asked for, so two PATCHes never race and the
 * later choice is always the one stored. A glyph or colour change shows at once and is rolled back
 * if the write fails; once it succeeds, the own values come from the PATCH response, which is
 * what the server actually stored.
 */
export function useSphereVisual(
  sphere: SphereVisualSphere,
  schemaType: SphereVisualSchemaType,
  options: UseSphereVisualOptions = {}
): UseSphereVisualResult {
  const onSphereChangedRef = useRef(options.onSphereChanged);
  onSphereChangedRef.current = options.onSphereChanged;
  const apiClients = useApiClientsOrNull();
  const logAnalyticsEvent = useAnalytics();
  const graphClient = apiClients?.graphClient;
  const sphereId = sphere.id;
  const sphereType = schemaType.system_name;

  const baseOwn = useMemo(() => readOwnSphereGlyph(sphere.properties), [sphere.properties]);
  const [localOwn, setLocalOwn] = useState<{ sphereId: string; own: SphereOwnGlyph | null } | null>(null);
  const own = localOwn?.sphereId === sphereId ? localOwn.own : baseOwn;
  // Queued writes read the latest own values before React re-renders, so the ref moves with every set.
  const ownRef = useRef(own);
  ownRef.current = own;
  const commitOwn = useCallback(
    (next: SphereOwnGlyph | null) => {
      ownRef.current = next;
      setLocalOwn({ sphereId, own: next });
    },
    [sphereId]
  );

  useEffect(() => {
    setLocalOwn(null);
  }, [sphere.properties]);

  const [status, setStatus] = useState<SphereVisualStatus>('idle');
  const [operation, setOperation] = useState<SphereVisualOperation | null>(null);
  const [error, setError] = useState<SphereVisualError | null>(null);
  const [undoEntry, setUndoEntry] = useState<UndoEntry | null>(null);
  const undoRef = useRef(undoEntry);
  undoRef.current = undoEntry;
  const retryRef = useRef<(() => SphereVisualWrite) | null>(null);
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());

  const typeGlyph = useMemo(
    () => ({ glyph: schemaType.glyph, glyphColor: schemaType.glyph_color }),
    [schemaType.glyph, schemaType.glyph_color]
  );
  const typeGlyphRef = useRef(typeGlyph);
  typeGlyphRef.current = typeGlyph;
  const resolved = useMemo(() => resolveSphereGlyph(own, typeGlyph), [own, typeGlyph]);

  // Upload is offered from the same slot config graph-service checks before it accepts one. A config
  // that cannot be read offers no upload: the server could not check the slot either.
  const schemaClient = apiClients?.schemaClient;
  const [avatarSlot, setAvatarSlot] = useState<{
    sphereType: string;
    limits: SphereVisualPictureLimits | null;
  } | null>(null);
  useEffect(() => {
    if (!schemaClient || !sphereType || !sphereId) return undefined;
    let current = true;
    getSphereSchemaConfig(schemaClient, sphereType)
      .then((config) => {
        const slot = config.slots?.avatar;
        if (current) {
          setAvatarSlot({ sphereType, limits: slot ? { mimeTypes: slot.mimeTypes, maxSize: slot.maxSize } : null });
        }
      })
      .catch(() => {
        if (current) setAvatarSlot({ sphereType, limits: null });
      });
    return () => {
      current = false;
    };
  }, [schemaClient, sphereType, sphereId]);
  const pictureLimits = avatarSlot?.sphereType === sphereType ? avatarSlot.limits : null;
  const canUploadPicture = pictureLimits !== null;

  // The picture is read from the sphere cache, and read again after each picture write: the
  // wrappers clear the cached entry, so the next read carries what the server now holds.
  const [pictureRead, setPictureRead] = useState<{ sphereId: string; url?: string } | null>(null);
  const [pictureRevision, setPictureRevision] = useState(0);
  // An uploaded picture being checked; `attempt` counts the re-reads made so far, and each upload
  // starts a new `generation`, so a re-read sent for an earlier upload never ends this one's check.
  const [check, setCheck] = useState<{ sphereId: string; attempt: number; generation: number } | null>(null);
  const checkGenerationRef = useRef(0);
  /** Records a read; a read that returns a picture ends the check. */
  const pictureWasRead = useCallback((readFor: string, url: string | undefined) => {
    setPictureRead({ sphereId: readFor, url });
    if (url) setCheck((c) => (c?.sphereId === readFor ? null : c));
  }, []);
  useEffect(() => {
    if (!graphClient || !sphereId) return undefined;
    let current = true;
    globalSphereCache
      .getSphereData(sphereId, graphClient)
      .then((data) => {
        if (current) pictureWasRead(sphereId, data.thumbnailUrl || undefined);
      })
      .catch(() => {
        if (current) pictureWasRead(sphereId, undefined);
      });
    return () => {
      current = false;
    };
  }, [graphClient, sphereId, pictureRevision, pictureWasRead]);
  const pictureUrl = pictureRead?.sphereId === sphereId ? pictureRead.url : undefined;
  const pictureResolving = !!graphClient && !!sphereId && pictureRead?.sphereId !== sphereId;
  const hasPictureRef = useRef(!!pictureUrl);
  hasPictureRef.current = !!pictureUrl;

  // While a picture is being checked, it is re-read past the cache on a bounded backoff. The timer
  // belongs to this sphere: it is cleared on unmount, and once the sphere changes the check no
  // longer matches, so no further re-read starts.
  const checkAttempt = check?.sphereId === sphereId ? check.attempt : null;
  const checkGeneration = check?.sphereId === sphereId ? check.generation : null;
  const checkStopped = checkAttempt !== null && checkAttempt >= PICTURE_CHECK_BACKOFF_MS.length;
  useEffect(() => {
    if (!graphClient || !sphereId || checkAttempt === null || checkGeneration === null || checkStopped) {
      return undefined;
    }
    let current = true;
    const attempted = () =>
      setCheck((c) =>
        c?.sphereId === sphereId && c.generation === checkGeneration ? { ...c, attempt: checkAttempt + 1 } : c
      );
    const timer = setTimeout(() => {
      rereadSphereAvatar(graphClient, sphereId)
        .then((data) => {
          if (!current) return;
          const url = data.thumbnailUrl || undefined;
          pictureWasRead(sphereId, url);
          if (url) onSphereChangedRef.current?.(sphereId);
          else attempted();
        })
        .catch(() => {
          if (current) attempted();
        });
    }, PICTURE_CHECK_BACKOFF_MS[checkAttempt]);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [graphClient, sphereId, checkAttempt, checkGeneration, checkStopped, pictureWasRead]);

  /** A removed picture is gone at once; an uploaded one is checked until a read returns it. */
  const pictureChanged = useCallback(
    (removed: boolean) => {
      if (removed) setPictureRead({ sphereId, url: undefined });
      checkGenerationRef.current += 1;
      setCheck(removed ? null : { sphereId, attempt: 0, generation: checkGenerationRef.current });
      setPictureRevision((r) => r + 1);
    },
    [sphereId]
  );

  const enqueue = useCallback((task: () => SphereVisualWrite): SphereVisualWrite => {
    const next = queueRef.current.then(task);
    queueRef.current = next.catch(() => undefined);
    return next;
  }, []);

  const run = useCallback(
    async (kind: SphereVisualOperation, op: () => Promise<void>): Promise<boolean> => {
      setStatus('saving');
      setOperation(kind);
      setError(null);
      retryRef.current = null;
      try {
        await op();
        setStatus('idle');
        return true;
      } catch (err) {
        const errorKind = classifySphereVisualError(err);
        const reason = sphereVisualErrorReason(err);
        const retryAt = errorKind === 'rateLimited' ? sphereVisualErrorRetryAt(err) : undefined;
        setError({ kind: errorKind, operation: kind, ...(reason && { reason }), ...(retryAt && { retryAt }) });
        setStatus(errorKind === 'readOnly' ? 'readOnly' : 'error');
        return false;
      } finally {
        setOperation(null);
      }
    },
    []
  );

  const saveSeqRef = useRef(0);
  const [lastSaveId, setLastSaveId] = useState<number | null>(null);
  const saved = useCallback(
    (change: SphereVisualChange, entry: UndoEntry | null): SphereVisualSaved => {
      undoRef.current = entry;
      setUndoEntry(entry);
      saveSeqRef.current += 1;
      const id = saveSeqRef.current;
      setLastSaveId(id);
      logAnalyticsEvent('sphere_visual_changed', sphereId, { change, sphereType });
      onSphereChangedRef.current?.(sphereId);
      return { id, change, undoable: entry !== null };
    },
    [logAnalyticsEvent, sphereId, sphereType]
  );

  /** Writes `patch` now; callers run it inside the queue. */
  const writeGlyph = useCallback(
    async (patch: SphereOwnGlyph, change: SphereVisualChange): SphereVisualWrite => {
      if (!graphClient) return null;
      const previous = ownRef.current;
      commitOwn(applyPatch(previous, patch));
      const ok = await run('glyph', async () => {
        const stored = await setSphereGlyph(graphClient, sphereId, patch);
        commitOwn(readOwnSphereGlyph(stored.properties));
      });
      if (!ok) {
        commitOwn(previous);
        retryRef.current = () => enqueue(() => writeGlyph(patch, change));
        return null;
      }
      const restore: SphereOwnGlyph = {};
      if ('glyph' in patch) restore.glyph = previous?.glyph ?? null;
      if ('glyphColor' in patch) restore.glyphColor = previous?.glyphColor ?? null;
      return saved(change, { kind: 'glyph', restore });
    },
    [graphClient, sphereId, commitOwn, run, enqueue, saved]
  );

  const apply = useCallback(
    (next: PickerValue): SphereVisualWrite =>
      enqueue(() => {
        const effective = resolveSphereGlyph(ownRef.current, typeGlyphRef.current);
        const patch: SphereOwnGlyph = {};
        if (next.glyph && next.glyph !== effective.glyph) patch.glyph = next.glyph;
        if (next.glyphColor && !sameColor(next.glyphColor, effective.glyphColor)) {
          patch.glyphColor = next.glyphColor;
        }
        if (!('glyph' in patch) && !('glyphColor' in patch)) return Promise.resolve(null);
        return writeGlyph(patch, glyphChangeOf(patch));
      }),
    [enqueue, writeGlyph]
  );

  const setGlyph = useCallback((glyph: string) => apply({ glyph }), [apply]);
  const setColour = useCallback((glyphColor: GlyphColor) => apply({ glyphColor }), [apply]);

  const resetNow = useCallback(async (): SphereVisualWrite => {
    if (!graphClient) return null;
    const previous = ownRef.current;
    if (!previous) return null;
    commitOwn(null);
    const ok = await run('glyph', async () => {
      const stored = await resetSphereGlyph(graphClient, sphereId);
      commitOwn(readOwnSphereGlyph(stored.properties));
    });
    if (!ok) {
      commitOwn(previous);
      retryRef.current = () => enqueue(resetNow);
      return null;
    }
    const restore: SphereOwnGlyph = {};
    if (previous.glyph) restore.glyph = previous.glyph;
    if (previous.glyphColor) restore.glyphColor = previous.glyphColor;
    return saved('reset', { kind: 'glyph', restore });
  }, [graphClient, sphereId, commitOwn, run, enqueue, saved]);

  const reset = useCallback(() => enqueue(resetNow), [enqueue, resetNow]);

  const uploadNow = useCallback(
    async (file: File): SphereVisualWrite => {
      if (!graphClient || !canUploadPicture) return null;
      const replacing = hasPictureRef.current;
      const ok = await run('upload', async () => {
        await uploadSphereAvatar(graphClient, sphereId, file);
      });
      if (!ok) {
        retryRef.current = () => enqueue(() => uploadNow(file));
        return null;
      }
      pictureChanged(false);
      return replacing ? saved('picture_replaced', null) : saved('picture_added', { kind: 'picture_added' });
    },
    [graphClient, canUploadPicture, sphereId, run, enqueue, saved, pictureChanged]
  );

  const uploadPicture = useCallback((file: File) => enqueue(() => uploadNow(file)), [enqueue, uploadNow]);

  const removeNow = useCallback(async (): SphereVisualWrite => {
    if (!graphClient || !canUploadPicture) return null;
    const ok = await run('remove', async () => {
      await deleteSphereAvatar(graphClient, sphereId);
    });
    if (!ok) {
      retryRef.current = () => enqueue(removeNow);
      return null;
    }
    pictureChanged(true);
    return saved('picture_removed', null);
  }, [graphClient, canUploadPicture, sphereId, run, enqueue, saved, pictureChanged]);

  const removePicture = useCallback(() => enqueue(removeNow), [enqueue, removeNow]);

  const undoNow = useCallback(async (): SphereVisualWrite => {
    const entry = undoRef.current;
    if (!graphClient || !entry) return null;
    const previous = ownRef.current;
    if (entry.kind === 'glyph') commitOwn(applyPatch(previous, entry.restore));
    const ok = await run(entry.kind === 'picture_added' ? 'remove' : 'glyph', async () => {
      if (entry.kind === 'picture_added') {
        await deleteSphereAvatar(graphClient, sphereId);
      } else {
        const stored = await setSphereGlyph(graphClient, sphereId, entry.restore);
        commitOwn(readOwnSphereGlyph(stored.properties));
      }
    });
    if (!ok) {
      if (entry.kind === 'glyph') commitOwn(previous);
      retryRef.current = () => enqueue(undoNow);
      return null;
    }
    if (entry.kind === 'picture_added') pictureChanged(true);
    return saved('undo', null);
  }, [graphClient, sphereId, commitOwn, run, enqueue, saved, pictureChanged]);

  const undo = useCallback(() => enqueue(undoNow), [enqueue, undoNow]);

  const retry = useCallback((): SphereVisualWrite => (retryRef.current ? retryRef.current() : Promise.resolve(null)), []);

  return {
    resolved,
    own,
    picture: {
      url: pictureUrl,
      pending: operation !== null,
      resolving: pictureResolving,
      checking: checkAttempt !== null,
      checkStopped,
    },
    status,
    operation,
    error,
    canUploadPicture,
    pictureLimits,
    canUndo: undoEntry !== null,
    lastSaveId,
    apply,
    setGlyph,
    setColour,
    reset,
    uploadPicture,
    removePicture,
    undo,
    retry,
  };
}
